import React, { useMemo, useState } from "react";
import Head from "next/head";
import styles from "./GridGallery.module.scss";
import { ProductImages, getGalleryObjects } from "../utils";
import MediaMedium from "../components/Media/MediaMedium";
import { ZoomModal } from "../components/ZoomModal/ZoomModal";
import { Options } from "./GridGallery";
import { Video } from "../../../types/prodcatBaseTypes";
import { dispatchAnalyticsEvent, imageProfileGenerator } from "../../../utils";
import { CarouselGalleryUI } from "../CarouselGallery/CarouselGalleryUI";
import { useGalleryContext } from "./GridGalleryContext";
import { getZoomAnalyticsPayload } from "../components/Analytics/AdobeAnalytics";
import { SocialProofMessages } from "../components/SocialProofMessages/SocialProofMessages";

type GridGalleryUIProps = {
  /**
   * A string representing the title of the gallery.
   */
  title: string;
  /**
   * An object representing the product images in the gallery.
   */
  images: ProductImages;
  /**
   * An array of Video objects representing the videos in the gallery.
   */
  videos: Video[];
  /**
   * An optional object representing the options for the gallery.
   */
  options?: Options;
  /**
   * A string representing the sizes available in the gallery.
   */
  sizes: string;
  /**
   * An optional flag passed from experience to show social proof messages
   */
  hasSocialProofMessagesExperience?: boolean;
  /**
   * An optional object representing options for Social Proof Messages
   */
  socialProofMessagesProperties?: {
    productId: string;
    brand: string;
    availableToOrder: boolean;
    appId: string;
    withClose?: boolean;
  };
  hasSuperZoom?: boolean;
};

export const GridGalleryUI = ({
  title,
  images,
  videos,
  sizes,
  hasSocialProofMessagesExperience = false,
  socialProofMessagesProperties,
  hasSuperZoom,
}: GridGalleryUIProps) => {
  const [isZoomModalOpen, setZoomModalOpen] = useState(false);

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const { images: galleryImages, videos: galleryVideos } = useMemo(
    () => getGalleryObjects(title, images, videos),
    [title, images, videos],
  );

  const { product, enableAnalytics, selectedVariant } = useGalleryContext() || {
    product: null,
    enableAnalytics: false,
    selectedVariant: null,
  };

  const openZoomModal = (
    event: React.MouseEvent<HTMLImageElement>,
    imageSrc: string,
  ) => {
    const { target: image } = event;
    if (image) {
      setSelectedImageIndex(
        galleryImages.findIndex((go) => go.imageUrl === imageSrc),
      );
      setZoomModalOpen(true);
      enableAnalytics &&
        dispatchAnalyticsEvent(
          "pdp.interaction",
          getZoomAnalyticsPayload("zoom", product, selectedVariant),
          {},
        );
    }
  };

  const closeZoomModal = () => {
    setZoomModalOpen(false);
  };

  return (
    <div id="image-gallery" className={styles.gridGalleryWrapper}>
      {hasSocialProofMessagesExperience && socialProofMessagesProperties && (
        <SocialProofMessages
          {...socialProofMessagesProperties}
          className="grid-gallery-instance"
        />
      )}
      <div data-testid="gallery" className={styles.gallery}>
        <Head>
          <link
            rel="preload"
            as="image"
            href={images.primary}
            imageSrcSet={imageProfileGenerator(images.primary)}
            imageSizes={sizes}
          />
        </Head>
        <MediaMedium
          openModal={openZoomModal}
          galleryObjects={[...galleryImages, ...galleryVideos]}
        />

        {isZoomModalOpen && (
          <ZoomModal
            closeZoomModal={closeZoomModal}
            mediaItems={galleryImages}
            selectedIndex={selectedImageIndex}
            setSelectedIndex={setSelectedImageIndex}
            hasSuperZoom={hasSuperZoom}
          />
        )}
      </div>
      <div className={styles.gridGalleryCarousel}>
        <CarouselGalleryUI
          title={title}
          images={images}
          videos={videos}
          sizes={sizes}
          hasSuperZoom={hasSuperZoom}
        />
      </div>
    </div>
  );
};
